import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box} from "@mui/system";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import {ReactComponent as BackIcon} from "../Icons/back.svg";
import {catalogItemGalleryStackMediaQuery} from "../../utils/compositionMediaUtils";
import {
    COMPOSITION_MEDIA_THUMBNAIL_GAP,
    COMPOSITION_MEDIA_THUMBNAIL_SIZE_COMPACT,
    compositionMediaThumbnailItemSx,
    compositionMediaThumbnailNavButtonNextHorizontalSx,
    compositionMediaThumbnailNavButtonNextVerticalSx,
    compositionMediaThumbnailNavButtonPrevHorizontalSx,
    compositionMediaThumbnailNavButtonPrevVerticalSx,
    compositionMediaThumbnailNavButtonSx,
    compositionMediaThumbnailsListSx,
    compositionMediaThumbnailsRailSx,
} from "./compositionMediaLayoutSx";
import {createResizeObserverScheduler} from "../../utils/scheduleResizeObserverCallback";

const THUMBNAIL_NAV_ICON_COLOR = "#3B8BEC";
const THUMBNAIL_NAV_ICON_WIDTH = "14px";
const THUMBNAIL_NAV_ICON_HEIGHT = "21px";

export function CompositionThumbnailsCarousel({
    pictures,
    selectedPictureId,
    onSelectPicture,
    getThumbnailUrl,
    emptyContent = null,
    thumbnailItemSx = compositionMediaThumbnailItemSx,
    maxHeight,
    thumbnailRailHeight = COMPOSITION_MEDIA_THUMBNAIL_SIZE_COMPACT,
}) {
    const railRef = useRef(null);
    const listRef = useRef(null);
    const isHorizontal = useMediaQuery(catalogItemGalleryStackMediaQuery);
    const compactThumbnailHeight = `${thumbnailRailHeight}px`;
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [overflows, setOverflows] = useState(false);

    const updateScrollButtons = useCallback(() => {
        const el = listRef.current;
        if (!el) {
            setOverflows(false);
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        if (isHorizontal) {
            const hasOverflow = el.scrollWidth > el.clientWidth + 1;
            setOverflows(hasOverflow);
            setCanScrollPrev(el.scrollLeft > 1);
            setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
            return;
        }
        const hasOverflow = el.scrollHeight > el.clientHeight + 1;
        setOverflows(hasOverflow);
        setCanScrollPrev(el.scrollTop > 1);
        setCanScrollNext(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, [isHorizontal]);

    useEffect(() => {
        const listEl = listRef.current;
        const railEl = railRef.current;
        if (!listEl) {
            return undefined;
        }
        let scrollScheduler = null
        const scheduleScrollButtonsUpdate = createResizeObserverScheduler(updateScrollButtons)
        scrollScheduler = scheduleScrollButtonsUpdate
        scheduleScrollButtonsUpdate()
        const resizeObserver = new ResizeObserver(scheduleScrollButtonsUpdate)
        resizeObserver.observe(listEl)
        if (railEl) {
            resizeObserver.observe(railEl)
        }
        listEl.addEventListener("scroll", updateScrollButtons, {passive: true});
        return () => {
            scrollScheduler?.cancel()
            resizeObserver.disconnect();
            listEl.removeEventListener("scroll", updateScrollButtons);
        };
    }, [pictures.length, isHorizontal, maxHeight, updateScrollButtons]);

    useEffect(() => {
        if (selectedPictureId == null || !listRef.current) {
            return;
        }
        const selectedEl = listRef.current.querySelector(
            `[data-picture-id="${selectedPictureId}"]`,
        );
        selectedEl?.scrollIntoView({block: "nearest", inline: "nearest", behavior: "smooth"});
    }, [selectedPictureId, isHorizontal]);

    const scrollByStep = (direction) => {
        const el = listRef.current;
        if (!el) {
            return;
        }
        const firstItem = el.querySelector("[data-thumbnail-item]");
        const step = (isHorizontal
            ? firstItem?.offsetWidth
            : firstItem?.offsetHeight) ?? 112;
        const delta = (step + COMPOSITION_MEDIA_THUMBNAIL_GAP) * direction;
        if (isHorizontal) {
            el.scrollBy({left: delta, behavior: "smooth"});
            return;
        }
        el.scrollBy({top: delta, behavior: "smooth"});
    };

    const renderNavButton = (direction, Icon, positionSx, label, iconSx = {}) => (
        <IconButton
            size="small"
            onClick={() => scrollByStep(direction)}
            disabled={direction < 0 ? !canScrollPrev : !canScrollNext}
            sx={{
                ...compositionMediaThumbnailNavButtonSx,
                ...positionSx,
            }}
            aria-label={label}
        >
            <Box
                component={Icon}
                sx={{
                    width: THUMBNAIL_NAV_ICON_WIDTH,
                    height: THUMBNAIL_NAV_ICON_HEIGHT,
                    display: "block",
                    flexShrink: 0,
                    "& path": {
                        stroke: THUMBNAIL_NAV_ICON_COLOR,
                    },
                    ...iconSx,
                }}
            />
        </IconButton>
    );

    const listContent = pictures.length === 0
        ? emptyContent
        : pictures.map((picture) => {
            const thumbnailUrl = getThumbnailUrl(picture);
            const isSelected = picture.id === selectedPictureId;
            return (
                <Box
                    key={picture.id}
                    data-thumbnail-item
                    data-picture-id={picture.id}
                    onClick={() => onSelectPicture(picture.id)}
                    sx={{
                        ...thumbnailItemSx,
                        cursor: thumbnailUrl ? "pointer" : "default",
                        border: (theme) => isSelected
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.primary.stroke}`,
                        backgroundColor: (theme) => theme.palette.background.grey,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {thumbnailUrl ? (
                        <Box
                            component="img"
                            src={thumbnailUrl}
                            alt=""
                            sx={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                            }}
                        />
                    ) : null}
                </Box>
            );
        });

    const prevPositionSx = isHorizontal
        ? compositionMediaThumbnailNavButtonPrevHorizontalSx
        : compositionMediaThumbnailNavButtonPrevVerticalSx;
    const nextPositionSx = isHorizontal
        ? compositionMediaThumbnailNavButtonNextHorizontalSx
        : compositionMediaThumbnailNavButtonNextVerticalSx;
    const prevIconSx = isHorizontal ? {} : {transform: "rotate(90deg)"};
    const nextIconSx = isHorizontal
        ? {transform: "rotate(180deg)"}
        : {transform: "rotate(-90deg)"};

    const listSx = {
        ...compositionMediaThumbnailsListSx,
        ...(isHorizontal ? {
            flex: "none",
            height: compactThumbnailHeight,
            maxHeight: compactThumbnailHeight,
        } : {}),
        ...(!isHorizontal && maxHeight != null ? {
            maxHeight: `${maxHeight}px`,
            height: "auto",
        } : {}),
    };

    const railSx = {
        ...compositionMediaThumbnailsRailSx,
        ...(isHorizontal ? {
            flex: "none",
            height: compactThumbnailHeight,
            maxHeight: compactThumbnailHeight,
        } : {}),
    };

    return (
        <Box ref={railRef} sx={railSx}>
            <Box ref={listRef} sx={listSx}>
                {listContent}
            </Box>
            {overflows ? (
                <>
                    {renderNavButton(
                        -1,
                        BackIcon,
                        prevPositionSx,
                        "Предыдущее изображение",
                        prevIconSx,
                    )}
                    {renderNavButton(
                        1,
                        BackIcon,
                        nextPositionSx,
                        "Следующее изображение",
                        nextIconSx,
                    )}
                </>
            ) : null}
        </Box>
    );
}
